import { CommonModule } from '@angular/common';
import {
  Component,
  input,
  output,
  signal,
  computed,
  forwardRef,
  ChangeDetectionStrategy,
  ElementRef,
  viewChild,
} from '@angular/core';
import { AfterViewInit } from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';

import { VOneIconComponent } from '@shared-components';
import { IconType } from '@shared-enums';
import { InputType, SelectOption, AutocompleteOption } from '@shared-models';

@Component({
  selector: 'v-one-input',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    forwardRef(() => VOneIconComponent),
  ],
  templateUrl: './v-one-input.component.html',
  styleUrls: ['./v-one-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => VOneInputComponent),
      multi: true,
    },
  ],
})
export class VOneInputComponent implements ControlValueAccessor, AfterViewInit {
  readonly ICON_TYPE = IconType;

  type = input<InputType>('text');
  label = input<string>('');
  placeholder = input<string>('');
  leftIcon = input<string>('');
  disabled = input<boolean>(false);
  readonly = input<boolean>(false);
  required = input<boolean>(false);
  clearable = input<boolean>(true);
  errorMessage = input<string>('');
  options = input<SelectOption[]>([]);
  autocompleteOptions = input<AutocompleteOption[]>([]);
  displayWith = input<((value: unknown) => string) | undefined>(undefined);
  dropdownPosition = input<'up' | 'down'>('down');

  min = input<number | undefined>(undefined);
  max = input<number | undefined>(undefined);
  step = input<number>(1);

  rows = input<number>(8);
  maxlength = input<number | undefined>(undefined);

  className = input<string>('');

  valueChange = output<string | number>();
  cleared = output<void>();
  focused = output<void>();
  blurred = output<void>();
  autocompleteSelected = output<unknown>();

  readonly value = signal<string | number | null | undefined>('');
  readonly isFocused = signal<boolean>(false);
  readonly isTouched = signal<boolean>(false);
  readonly isDropdownOpen = signal<boolean>(false);
  readonly isAutocompleteOpen = signal<boolean>(false);
  readonly autocompleteDisplayValue = signal<string>('');

  private readonly formDisabled = signal<boolean>(false);

  readonly isDisabled = computed(() => {
    return this.disabled() || this.formDisabled();
  });

  readonly hasValue = computed(() => {
    const val = this.value();
    return val !== null && val !== undefined && val !== '';
  });

  readonly isIOS = signal<boolean>(
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1),
  );

  readonly showClearButton = computed(() => {
    const inputType = this.type();
    const hasTextValue = inputType === 'text' || inputType === 'textarea';
    const hasAutocompleteValue =
      inputType === 'autocomplete' && this.autocompleteDisplayValue();

    return (
      this.clearable() &&
      (hasTextValue || hasAutocompleteValue) &&
      this.hasValue() &&
      this.isFocused() &&
      !this.isDisabled()
    );
  });

  /**
   * The message is shown whenever the consumer provides one: the form is
   * what knows whether the field is due an error.
   */
  readonly hasError = computed(() => !!this.errorMessage());

  readonly dropdownOptions = computed(() => {
    if (this.type() !== 'select') return [];

    const placeholderText = this.placeholder() || 'Select an option';
    const placeholderOption: SelectOption = {
      value: '',
      label: placeholderText,
      disabled: false,
    };

    return [placeholderOption, ...this.options()];
  });

  readonly inputClasses = computed(() => {
    const baseClass = 'v-one-input';
    const typeClass = `${baseClass}--${this.type()}`;
    const focusClass = this.isFocused() ? `${baseClass}--focused` : '';
    const errorClass = this.hasError() ? `${baseClass}--error` : '';
    const disabledClass = this.isDisabled() ? `${baseClass}--disabled` : '';
    const hasValueClass = this.hasValue() ? `${baseClass}--has-value` : '';
    const leftIconClass = this.leftIcon() ? `${baseClass}--with-left-icon` : '';
    const customClass = this.className();

    return [
      baseClass,
      typeClass,
      focusClass,
      errorClass,
      disabledClass,
      hasValueClass,
      leftIconClass,
      customClass,
    ]
      .filter(Boolean)
      .join(' ');
  });

  readonly inputElement = viewChild<ElementRef<HTMLInputElement>>('inputRef');
  readonly selectElement =
    viewChild<ElementRef<HTMLSelectElement>>('selectRef');
  readonly textareaElement =
    viewChild<ElementRef<HTMLTextAreaElement>>('inputRef');

  readonly autocompleteDropdownStyles = signal<{
    top?: string;
    left?: string;
    width?: string;
  }>({});

  /** Formatted text shown by the currency variant. */
  readonly currencyText = signal<string>('');

  readonly selectDropdownStyles = signal<{
    top?: string;
    bottom?: string;
    left?: string;
    width?: string;
  }>({});

  private onChange: (value: string | number | null) => void = (): void => {
    // Implemented by Angular Forms
  };
  private onTouched: () => void = (): void => {
    // Implemented by Angular Forms
  };

  writeValue(value: string | number | null): void {
    this.value.set(value ?? '');

    if (this.type() === 'currency') {
      this.syncCurrencyText(value);
    }

    // For autocomplete, update display value using displayWith function
    if (this.type() === 'autocomplete' && value) {
      const displayFn = this.displayWith();
      if (displayFn) {
        this.autocompleteDisplayValue.set(displayFn(value));
      } else if (typeof value === 'string') {
        this.autocompleteDisplayValue.set(value);
      }
    }
  }

  registerOnChange(fn: (value: string | number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.formDisabled.set(isDisabled);
  }

  /**
   * Formats the amount as it is typed (1234 -> 1.234) while the control
   * keeps a plain number, which is what a Float argument expects. Only
   * digits and a single decimal comma survive; the comma and its decimals
   * are kept verbatim so typing "10," does not fight the caret.
   */
  onCurrencyInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const [rawInteger, ...rawDecimals] = target.value
      .replace(/[^\d,]/g, '')
      .split(',');
    const integerDigits = rawInteger ?? '';
    const decimals =
      rawDecimals.length > 0 ? rawDecimals.join('').slice(0, 2) : null;

    if (integerDigits === '' && decimals === null) {
      this.currencyText.set('');
      this.writeCurrencyValue('');
      return;
    }

    const groupedInteger = this.groupThousands(integerDigits);
    this.currencyText.set(
      this.withCurrencySymbol(
        decimals === null ? groupedInteger : `${groupedInteger},${decimals}`,
      ),
    );

    const amount = Number(`${integerDigits || '0'}.${decimals || '0'}`);
    this.writeCurrencyValue(Number.isFinite(amount) ? amount : '');
  }

  private writeCurrencyValue(amount: number | ''): void {
    this.value.set(amount);
    this.onChange(amount === '' ? null : amount);
    this.valueChange.emit(amount);
  }

  private groupThousands(digits: string): string {
    return digits
      .replace(/^0+(?=\d)/, '')
      .replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  }

  /** Renders a value set from outside, e.g. when a form is patched. */
  private syncCurrencyText(value: string | number | null): void {
    if (value === null || value === '') {
      this.currencyText.set('');
      return;
    }

    const amount = Number(value);
    if (!Number.isFinite(amount)) {
      this.currencyText.set('');
      return;
    }

    const [integerPart, decimalPart] = amount.toFixed(2).split('.');
    const grouped = this.groupThousands(integerPart);
    this.currencyText.set(
      this.withCurrencySymbol(
        decimalPart === '00' ? grouped : `${grouped},${decimalPart}`,
      ),
    );
  }

  /**
   * The symbol travels with the amount, so the field reads as money while it
   * is typed. Only the text carries it: the form control keeps the number,
   * and typing strips everything that is not a digit before parsing.
   */
  private withCurrencySymbol(amount: string): string {
    return amount === '' ? '' : `$ ${amount}`;
  }

  onInputChange(event: Event): void {
    const target = event.target as HTMLInputElement | HTMLSelectElement;
    let newValue: string | number = target.value;

    // Type conversion for number inputs
    if (this.type() === 'number' && newValue !== '') {
      newValue = parseFloat(newValue);
      if (isNaN(newValue) || !isFinite(newValue)) {
        newValue = '';
      }
    }

    this.value.set(newValue);
    this.onChange(newValue);
    this.valueChange.emit(newValue);

    if (this.type() === 'textarea') {
      this.adjustTextareaHeight(
        event.target instanceof HTMLTextAreaElement ? event.target : undefined,
      );
    }
  }

  ngAfterViewInit(): void {
    // ensure textarea initial height fits content after view init
    if (this.type() === 'textarea') {
      this.adjustTextareaHeight();
    }
  }

  onFocus(): void {
    this.isFocused.set(true);
    this.focused.emit();
  }

  onBlur(): void {
    this.isFocused.set(false);
    this.isTouched.set(true);
    this.onTouched();
    this.blurred.emit();

    // Close dropdown on blur for select
    if (this.type() === 'select') {
      this.isDropdownOpen.set(false);
    }
  }

  onSelectClick(): void {
    if (this.type() === 'select' && !this.isDisabled()) {
      // Toggle dropdown state on each click
      this.isDropdownOpen.update((open) => {
        const newState = !open;
        if (newState) {
          this.updateSelectPosition();
        }
        return newState;
      });
    }
  }

  onOptionClick(option: SelectOption): void {
    if (option.disabled) return;

    this.value.set(option.value);
    this.onChange(option.value);
    this.onTouched();
    this.valueChange.emit(option.value);

    // Close dropdown after selection
    this.isDropdownOpen.set(false);
  }

  onClear(): void {
    if (!this.clearable()) return;

    this.value.set('');
    this.onChange('');
    this.valueChange.emit('');
    this.cleared.emit();

    this.isTouched.set(true);
    this.onTouched();

    this.focusInput();
  }

  onNumberIncrement(): void {
    if (this.type() !== 'number' || this.isDisabled()) return;

    const currentValue: number =
      typeof this.value() === 'number' ? (this.value() as number) : 0;
    const stepValue = this.step();
    let newValue = currentValue + stepValue;

    const maxValue = this.max();
    if (maxValue !== undefined && newValue > maxValue) {
      newValue = maxValue;
    }

    this.value.set(newValue);
    this.onChange(newValue);
    this.valueChange.emit(newValue);
  }

  onNumberDecrement(): void {
    if (this.type() !== 'number' || this.isDisabled()) return;

    const currentValue: number =
      typeof this.value() === 'number' ? (this.value() as number) : 0;
    const stepValue = this.step();
    let newValue = currentValue - stepValue;

    const minValue = this.min();
    if (minValue !== undefined && newValue < minValue) {
      newValue = minValue;
    }

    this.value.set(newValue);
    this.onChange(newValue);
    this.valueChange.emit(newValue);
  }

  toggleDropdown(): void {
    if (this.type() === 'select' && !this.isDisabled()) {
      this.isDropdownOpen.update((open) => {
        const newState = !open;
        if (newState) {
          this.updateSelectPosition();
        }
        return newState;
      });
    }
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this.type() === 'select') {
      this.isDropdownOpen.set(false);
    }

    if (event.key === 'Backspace' && !this.hasValue()) {
      this.onClear();
    }

    if (this.type() === 'number') {
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.onNumberIncrement();
      } else if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.onNumberDecrement();
      }
    }
  }

  private focusInput(): void {
    setTimeout(() => {
      const inputEl = this.inputElement();
      const selectEl = this.selectElement();

      if (inputEl) {
        inputEl.nativeElement.focus();
      } else if (selectEl) {
        selectEl.nativeElement.focus();
      }
    }, 0);
  }
  private adjustTextareaHeight(textareaElement?: HTMLTextAreaElement): boolean {
    try {
      let targetTextarea: HTMLTextAreaElement | null = null;

      if (textareaElement) {
        targetTextarea = textareaElement;
      } else {
        const childRef = this.textareaElement && this.textareaElement();
        if (childRef && childRef.nativeElement) {
          targetTextarea = childRef.nativeElement as HTMLTextAreaElement;
        }
      }

      if (!targetTextarea) return false;

      targetTextarea.style.height = 'auto';
      const contentHeight = targetTextarea.scrollHeight;
      if (!contentHeight) return false;
      targetTextarea.style.height = contentHeight + 'px';
      return true;
    } catch (error) {
      console.error('Error adjusting textarea height:', error);
      return false;
    }
  }

  getSelectedLabel(): string {
    if (this.type() !== 'select') return '';

    const currentValue = this.value();

    // If no value, show placeholder
    if (!currentValue || currentValue === '') {
      return this.placeholder() || 'Select an option';
    }

    // Find the selected option from the actual options (not including placeholder)
    const selectedOption = this.options().find(
      (opt) => opt.value === currentValue,
    );

    return selectedOption ? selectedOption.label : '';
  }

  onAutocompleteInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const inputValue = target.value;

    this.autocompleteDisplayValue.set(inputValue);
    this.value.set(inputValue);
    this.onChange(inputValue);
    this.valueChange.emit(inputValue);

    if (inputValue && inputValue.trim().length > 0) {
      this.isAutocompleteOpen.set(true);
      this.updateAutocompletePosition();
    } else {
      this.isAutocompleteOpen.set(false);
    }
  }

  private getAdjustedRect(rect: DOMRect): {
    top: number;
    bottom: number;
    left: number;
  } {
    const vv = window.visualViewport;
    return {
      top: rect.top + (vv?.offsetTop ?? 0),
      bottom: rect.bottom + (vv?.offsetTop ?? 0),
      left: rect.left + (vv?.offsetLeft ?? 0),
    };
  }

  private updateAutocompletePosition(): void {
    if (window.matchMedia('(pointer: coarse)').matches) {
      this.autocompleteDropdownStyles.set({});
      return;
    }

    const calculate = () => {
      const inputEl = this.inputElement();
      if (!inputEl) return;

      const rect = inputEl.nativeElement.getBoundingClientRect();
      const { bottom, left } = this.getAdjustedRect(rect);

      this.autocompleteDropdownStyles.set({
        top: `${bottom + 4}px`,
        left: `${left}px`,
        width: `${rect.width}px`,
      });
    };

    calculate();

    const vv = window.visualViewport;
    if (vv) {
      vv.addEventListener('resize', calculate);
      setTimeout(() => vv.removeEventListener('resize', calculate), 400);
    }
  }

  private updateSelectPosition(): void {
    setTimeout(() => {
      const selectEl = this.selectElement();
      if (!selectEl) return;

      const rect = selectEl.nativeElement.getBoundingClientRect();
      const { top, bottom, left } = this.getAdjustedRect(rect);
      const vvHeight = window.visualViewport?.height ?? window.innerHeight;

      if (this.dropdownPosition() === 'up') {
        this.selectDropdownStyles.set({
          bottom: `${vvHeight - top + 4}px`,
          left: `${left}px`,
          width: `${rect.width}px`,
        });
      } else {
        this.selectDropdownStyles.set({
          top: `${bottom + 4}px`,
          left: `${left}px`,
          width: `${rect.width}px`,
        });
      }
    }, 0);
  }

  onAutocompleteOptionClick(option: AutocompleteOption): void {
    const displayFn = this.displayWith();
    const displayText =
      option.label || (displayFn ? displayFn(option.value) : '');

    this.autocompleteDisplayValue.set(displayText);
    this.value.set(option.value as string | number | null | undefined);
    this.onChange(option.value as string | number | null);
    this.autocompleteSelected.emit(option.value);
    this.isAutocompleteOpen.set(false);
  }

  onAutocompleteBlur(): void {
    // Delay closing to allow option click to register
    setTimeout(() => {
      this.isAutocompleteOpen.set(false);
      this.onBlur();
    }, 200);
  }

  onAutocompleteFocus(): void {
    const currentValue = this.autocompleteDisplayValue();
    if (currentValue && currentValue.trim().length > 0) {
      this.isAutocompleteOpen.set(true);
      this.updateAutocompletePosition();
    }
    this.onFocus();
  }
}
